import { useEffect, useMemo, useState } from 'react';
import FullscreenLoader from '../components/FullscreenLoader';
import PaginatedTable from '../components/PaginatedTable';
import { useApi } from '../hooks/useApi';
import { API_BASE_URL } from '../config/env';
import '../styles/conversaciones-premium.css';
import '../styles/gestion-derivacion-premium.css';
import '../styles/trazabilidad-recetas-premium.css';
import '../styles/recetas-cruzadas-premium.css';
import { CwListSearch } from '../components/conversaciones/CwUiParts';
import { CrmStatusBadgeFrom } from '../components/CrmStatusBadge';
import { resolveRecetaCruzadaStatusBadge } from '../utils/crmStatusBadge';

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_MODES = {
  DNI: 'dni',
  ALL: 'all'
};

const STATUS_FILTER_CHIPS = [
  { id: 'all', label: 'Todos', cls: 'all', countKey: null },
  { id: 'con_ident', label: 'Con ident.', cls: 'assign', countKey: 'conIdentificador' },
  { id: 'cruce', label: 'Cruce', cls: 'aprobado', countKey: 'conCruce' },
  { id: 'sin_cruce', label: 'Sin cruce', cls: 'pendiente', countKey: 'sinCruce' }
];

function getStatus(row) {
  const hasIdent =
    row.rec_identificador_geclisa &&
    String(row.rec_identificador_geclisa).trim() !== '';
  const hasPlex = row.idreceta_plex != null;

  if (!hasIdent) return 'Sin validar';

  const identPrefix = String(row.rec_identificador_geclisa)
    .split('-')[0]
    .trim();
  const numReceta =
    row.numreceta_plex != null ? String(row.numreceta_plex).trim() : null;

  if (hasPlex && identPrefix && numReceta && identPrefix === numReceta) {
    return 'Cruce con Plex';
  }

  return 'Validada sin cruce';
}

function getStatusTag(status) {
  return <CrmStatusBadgeFrom badge={resolveRecetaCruzadaStatusBadge(status)} />;
}

function getKpis(rows) {
  if (!rows || rows.length === 0) {
    return {
      total: 0,
      emitidasSanatorio: 0,
      conIdentificador: 0,
      conCruce: 0,
      sinCruce: 0
    };
  }

  let emitidasSanatorio = 0;
  let conIdentificador = 0;
  let conCruce = 0;
  let sinCruce = 0;

  rows.forEach(r => {
    // Emitidas por sanatorio: tienen rec_enca_id_geclisa
    if (r.rec_enca_id_geclisa != null) emitidasSanatorio += 1;

    const hasIdent =
      r.rec_identificador_geclisa &&
      String(r.rec_identificador_geclisa).trim() !== '';
    if (hasIdent) conIdentificador += 1;

    const status = getStatus(r);
    if (status === 'Cruce con Plex') conCruce += 1;
    if (status === 'Validada sin cruce') sinCruce += 1;
  });

  return {
    total: rows.length,
    emitidasSanatorio,
    conIdentificador,
    conCruce,
    sinCruce
  };
}

function safeText(v) {
  if (v == null || v === '') return '—';
  return String(v);
}

function formatDate(date) {
  if (!date) return '—';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-AR');
}

function getDniMeta(dni) {
  if (!dni) return { formatted: null, digits: null };
  const digits = String(dni)
    .trim()
    .replace(/^0+/, '')
    .replace(/\D/g, '');
  if (!digits) return { formatted: null, digits: null };
  const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return { formatted, digits };
}

function formatDni(dni) {
  return getDniMeta(dni).formatted;
}

function normalizeDni(value) {
  if (!value) return '';
  return String(value).trim().replace(/\D/g, '').replace(/^0+/, '');
}

function csvDate(value) {
  if (!value) return '';
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return s;
  return d.toISOString().slice(0, 10);
}

function csvCell(value) {
  if (value == null) return '';
  const s = String(value);
  if (/[";\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildRecetasCsv(rows) {
  const headers = [
    'rec_fecha_geclisa',
    'obra_social_geclisa',
    'prestador_geclisa',
    'rec_identificador_geclisa',
    'numreceta_plex'
  ];
  const lines = [headers.join(';')];
  rows.forEach((r) => {
    lines.push([
      csvCell(csvDate(r.rec_fecha_geclisa)),
      csvCell(r.obra_social_geclisa),
      csvCell(r.prestador_geclisa),
      csvCell(r.rec_identificador_geclisa),
      csvCell(r.numreceta_plex)
    ].join(';'));
  });
  return lines.join('\r\n');
}

function RecetasCruzadas() {
  const [searchMode, setSearchMode] = useState(SEARCH_MODES.DNI);
  const [search, setSearch] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [pendingFromDate, setPendingFromDate] = useState('');
  const [pendingToDate, setPendingToDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [tableSearch, setTableSearch] = useState('');
  const [committedTableSearch, setCommittedTableSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const isAllMode = searchMode === SEARCH_MODES.ALL;
  const dniDigits = useMemo(() => normalizeDni(committedSearch), [committedSearch]);
  const canFetch = isAllMode || Boolean(dniDigits);

  const commitTableSearch = () => {
    const next = tableSearch.trim();
    if (next === committedTableSearch) return;
    setPage(1);
    setCommittedTableSearch(next);
  };

  const clearTableSearch = () => {
    setTableSearch('');
    if (committedTableSearch) {
      setPage(1);
      setCommittedTableSearch('');
    }
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === searchMode) return;
    setSearchMode(nextMode);
    setSearch('');
    setCommittedSearch('');
    setPage(1);
    setFromDate('');
    setToDate('');
    setPendingFromDate('');
    setPendingToDate('');
    setTableSearch('');
    setCommittedTableSearch('');
    setStatusFilter('all');
  };

  const handleApplyDateFilter = () => {
    setPage(1);
    setFromDate(pendingFromDate);
    setToDate(pendingToDate);
  };

  const handleClearDateFilter = () => {
    setPage(1);
    setPendingFromDate('');
    setPendingToDate('');
    setFromDate('');
    setToDate('');
  };

  const handleExportCsv = async () => {
    if (exporting) return;
    const expFrom = pendingFromDate || fromDate;
    const expTo = pendingToDate || toDate;
    setExportError('');
    setExporting(true);
    try {
      const params = new URLSearchParams({ mode: SEARCH_MODES.ALL, page: '1', pageSize: 'all' });
      if (expFrom) params.set('fromDate', expFrom);
      if (expTo) params.set('toDate', expTo);
      const res = await fetch(
        `${API_BASE_URL}/api/asociacion-recetas/y-facturacion?${params.toString()}`,
        { credentials: 'include' }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `Error ${res.status}`);
      const exportRows = json?.recetas?.data ?? [];
      if (exportRows.length === 0) {
        setExportError('No hay registros para el rango seleccionado.');
        return;
      }
      const csv = buildRecetasCsv(exportRows);
      const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = `${expFrom || 'inicio'}_${expTo || 'hoy'}`.replace(/[^0-9a-zA-Z_-]/g, '');
      a.href = url;
      a.download = `recetas_cruzadas_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err?.message || 'No se pudo generar el CSV.');
    } finally {
      setExporting(false);
    }
  };

  const endpoint = useMemo(() => {
    if (!canFetch) return null;
    const params = new URLSearchParams({
      mode: searchMode,
      page: String(page),
      pageSize: pageSize <= 0 ? 'all' : String(pageSize),
      includeItems: 'false'
    });
    if (searchMode === SEARCH_MODES.DNI) {
      params.set('search', dniDigits);
    }
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    if (committedTableSearch) params.set('q', committedTableSearch);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    const lightStatusOnly = searchMode === SEARCH_MODES.ALL
      && !fromDate
      && !toDate
      && !committedTableSearch
      && statusFilter !== 'all';
    if (lightStatusOnly) params.set('skipSummary', '1');
    return `/api/asociacion-recetas/y-facturacion?${params.toString()}`;
  }, [canFetch, committedTableSearch, dniDigits, fromDate, page, pageSize, searchMode, statusFilter, toDate]);

  const { data, loading, error } = useApi(endpoint);
  const { data: homeRecetasResponse } = useApi('/api/asociacion-recetas?page=1&limit=1');

  useEffect(() => {
    if (data && !loading && !error) setHasLoadedOnce(true);
  }, [data, loading, error]);

  const rows = useMemo(() => data?.recetas?.data ?? [], [data]);
  const summary = data?.meta?.summary || null;
  const pagination = data?.meta?.pagination || null;
  const dniMeta = getDniMeta(dniDigits);
  const formattedDni = dniMeta.formatted;
  const groupedRows = useMemo(() => {
    if (!rows || rows.length === 0) return [];

    const map = new Map();

    rows.forEach(r => {
      const key = r.rec_enca_id_geclisa || r.idreceta_plex || r.id_unico;
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });

    const out = [];

    map.forEach(group => {
      const base = group[0];

      const ident = base.rec_identificador_geclisa;
      const identPrefix =
        ident && String(ident).trim() !== ''
          ? String(ident).split(' ')[0].split('-')[0].trim()
          : '';

      let plexMatch = null;
      if (identPrefix) {
        plexMatch =
          group.find(g => {
            if (g.numreceta_plex == null) return false;
            const n = String(g.numreceta_plex).trim();
            return n !== '' && n === identPrefix;
          }) || null;
      }

      const synthetic = {
        ...base,
        idreceta_plex: plexMatch?.idreceta_plex ?? base.idreceta_plex,
        numreceta_plex: plexMatch?.numreceta_plex ?? base.numreceta_plex
      };

      const status = getStatus(synthetic);

      const prestadorSource =
        group.find(
          g =>
            g.prestador_geclisa ||
            g.medico_geclisa ||
            g.prestador_plex
        ) || base;

      const obraSource =
        group.find(
          g =>
            g.obra_social_geclisa ||
            g.obra_social_plex
        ) || base;

      out.push({
        ...base,
        prestador_geclisa: prestadorSource.prestador_geclisa ?? base.prestador_geclisa,
        medico_geclisa: prestadorSource.medico_geclisa ?? base.medico_geclisa,
        prestador_plex: prestadorSource.prestador_plex ?? base.prestador_plex,
        obra_social_geclisa: obraSource.obra_social_geclisa ?? base.obra_social_geclisa,
        obra_social_plex: obraSource.obra_social_plex ?? base.obra_social_plex,
        codcliente_plex: plexMatch?.codcliente_plex ?? base.codcliente_plex,
        numreceta_plex: plexMatch?.numreceta_plex ?? base.numreceta_plex,
        _status: status,
        _statusTag: getStatusTag(status)
      });
    });

    return out;
  }, [rows]);

  const enrichedRows = useMemo(
    () =>
      groupedRows.map(r => {
        const identPrefix = r.rec_identificador_geclisa
          ? String(r.rec_identificador_geclisa).split('-')[0].trim()
          : null;
        return {
          ...r,
          _identPrefix: identPrefix
        };
      }),
    [groupedRows]
  );

  const uniqueRows = useMemo(() => {
    if (!enrichedRows || enrichedRows.length === 0) return [];

    const seen = new Set();
    const out = [];

    enrichedRows.forEach(r => {
      // Mostramos recetas con encabezado GECLISA y también las que son sólo PLEX
      // (sin contraparte GECLISA), deduplicando por su identificador propio.
      let key;
      if (r.rec_enca_id_geclisa != null) {
        key = `GECLISA:${r.rec_enca_id_geclisa}`;
      } else if (r.idreceta_plex != null) {
        key = `PLEX:${r.idreceta_plex}`;
      } else {
        return;
      }
      if (seen.has(key)) return;
      seen.add(key);
      out.push(r);
    });

    return out;
  }, [enrichedRows]);

  const globalKpis = useMemo(() => {
    const stats = homeRecetasResponse?.stats;
    if (!stats) return null;
    const conIdentificador = stats.conIdentificador ?? 0;
    const emitidas = stats.emitidasSanatorio ?? 0;
    return {
      conIdentificador,
      conCruce: stats.cruceConPlex ?? 0,
      sinCruce: stats.sinCruceValidada ?? 0,
      sinValidar: Math.max(0, emitidas - conIdentificador)
    };
  }, [homeRecetasResponse]);

  const chipKpis = isAllMode && globalKpis ? globalKpis : null;

  const statusChipTotal = useMemo(() => {
    if (!chipKpis || statusFilter === 'all') return null;
    const chip = STATUS_FILTER_CHIPS.find((c) => c.id === statusFilter);
    if (!chip?.countKey) return null;
    return chipKpis[chip.countKey] ?? 0;
  }, [chipKpis, statusFilter]);

  const effectivePageSize = pageSize <= 0 ? (statusChipTotal || uniqueRows.length || 1) : pageSize;
  const totalRecords = pagination?.clientTotal && statusChipTotal != null
    ? statusChipTotal
    : (pagination?.totalRecords ?? uniqueRows.length);
  const totalPages = pagination?.clientTotal && statusChipTotal != null
    ? Math.max(1, Math.ceil(statusChipTotal / effectivePageSize))
    : (pagination?.totalPages ?? 1);
  const currentPage = pagination?.page ?? page;
  const kpis = useMemo(() => {
    if (chipKpis) return chipKpis;

    const hasDateFilters = Boolean(fromDate || toDate);
    const hasTextSearch = Boolean(committedTableSearch);

    if (summary) {
      const conIdentificador = summary.conIdentificador ?? 0;
      const total = summary.total ?? summary.emitidasSanatorio ?? 0;
      return {
        conIdentificador,
        conCruce: summary.conCruce ?? 0,
        sinCruce: summary.sinCruce ?? 0,
        sinValidar: Math.max(0, total - conIdentificador)
      };
    }

    const local = getKpis(uniqueRows);
    return {
      conIdentificador: local.conIdentificador,
      conCruce: local.conCruce,
      sinCruce: local.sinCruce,
      sinValidar: Math.max(0, local.total - local.conIdentificador)
    };
  }, [chipKpis, fromDate, toDate, committedTableSearch, summary, uniqueRows]);

  const handleStatusFilter = (next) => {
    if (next === statusFilter) return;
    setPage(1);
    setStatusFilter(next);
  };

  const columns = useMemo(
    () => [
      {
        key: 'paciente',
        label: 'Paciente / Afiliado',
        width: 260,
        render: (row) => {
          const apellido = (row.apellido_geclisa || '').trim();
          const nombre = (row.nombre_geclisa || '').trim();
          const nombreGeclisa = [apellido, nombre].filter(Boolean).join(', ');
          const nombrePlex = (row.nombre_plex || '').trim();
          const displayName = nombreGeclisa || nombrePlex || 'Sin nombre';

          const dni = formattedDni || formatDni(row.dni) || null;

          const initials =
            displayName
              .split(' ')
              .map(x => x[0])
              .filter(Boolean)
              .slice(0, 2)
              .join('')
              .toUpperCase() || '?';

          return (
            <div className="crm-rc-patient-cell">
              <div className="crm-gd-avatar">{initials}</div>
              <div style={{ minWidth: 0 }}>
                <div className="crm-gd-card__name">{displayName}</div>
                {dni ? (
                  <div className="crm-gd-card__meta" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    DNI {dni}
                  </div>
                ) : null}
              </div>
            </div>
          );
        }
      },
      {
        key: 'rec_enca_id_geclisa',
        label: 'Receta GECLISA',
        width: 140,
        render: (row) => (
          <div style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 600 }}>
              {row.rec_enca_id_geclisa ?? '—'}
            </div>
            <div style={{ color: '#6b7280', marginTop: 2 }}>
              {formatDate(row.rec_fecha_geclisa) || 'Sin fecha'}
            </div>
          </div>
        )
      },
      {
        key: 'fecha_visible',
        label: 'Fecha emisión',
        width: 130,
        render: (row) => formatDate(row.rec_fecha_geclisa || row.fechaemision_plex)
      },
      {
        key: 'medico_prestador',
        label: 'Médico / Prestador',
        width: 180,
        render: (row) => safeText(row.prestador_geclisa || row.medico_geclisa || row.prestador_plex)
      },
      {
        key: 'obra_social_visible',
        label: 'Cobertura',
        width: 160,
        render: (row) => safeText(row.obra_social_geclisa || row.obra_social_plex)
      },
      {
        key: 'estado_visible',
        label: 'Estado',
        width: 160,
        render: (row) => row._statusTag
      }
    ],
    [formattedDni]
  );

  const extraColumns = useMemo(
    () => [
      {
        key: 'codcliente_plex',
        label: 'Cliente (farmacia)',
        render: (row) => (
          <span className="crm-rc-expand-value crm-rc-expand-value--client">
            {safeText(row.codcliente_plex)}
          </span>
        )
      },
      {
        key: 'numreceta_plex',
        label: 'N° receta PLEX',
        render: (row) => (
          <span className="crm-rc-expand-value crm-rc-expand-value--plex">
            {safeText(row.numreceta_plex)}
          </span>
        )
      },
      {
        key: 'rec_identificador_geclisa',
        label: 'Identificador GECLISA',
        render: (row) => {
          const raw = row.rec_identificador_geclisa;
          if (!raw) return <span className="crm-rc-expand-value is-empty">—</span>;
          const firstPart = String(raw).split('-')[0].trim();
          return (
            <span className="crm-rc-expand-value crm-rc-expand-value--geclisa">
              {firstPart || String(raw)}
            </span>
          );
        }
      }
    ],
    []
  );

  const commitDniSearch = () => {
    setPage(1);
    setCommittedSearch(search);
  };

  return (
    <>
      {loading && canFetch && !hasLoadedOnce && (
        <FullscreenLoader
          fullscreen={true}
          text={
            isAllMode
              ? 'Cargando recetas cruzadas GECLISA ↔ PLEX…'
              : 'Cargando recetas cruzadas GECLISA ↔ PLEX…'
          }
        />
      )}

      <section className="crm-gd-page crm-rc-page crm-pbody">
        <div className="crm-rc-main">
          <div className="crm-gd-toolbar crm-rc-toolbar">
            <div className="crm-rc-mode-switch">
              <button
                type="button"
                className={`crm-rc-mode-btn${!isAllMode ? ' crm-rc-mode-btn--active' : ''}`}
                onClick={() => handleModeChange(SEARCH_MODES.DNI)}
              >
                Buscar por DNI
              </button>
              <button
                type="button"
                className={`crm-rc-mode-btn${isAllMode ? ' crm-rc-mode-btn--active' : ''}`}
                onClick={() => handleModeChange(SEARCH_MODES.ALL)}
              >
                Todos los registros
              </button>
            </div>

            {!isAllMode ? (
              <div className="crm-rc-search-row">
                <CwListSearch
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por DNI…"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitDniSearch();
                    }
                  }}
                />
                <button type="button" className="crm-gd-btn crm-gd-btn--primary" onClick={commitDniSearch}>
                  Buscar
                </button>
              </div>
            ) : null}

            {canFetch && !error && !isAllMode ? (
              <div className={`crm-rtz-kpis crm-rc-kpis${loading ? ' is-loading' : ''}`}>
                <div className="crm-rtz-kpi is-assign" title="Recetas con identificador GECLISA">
                  <span className="crm-rtz-kpi__num">{kpis.conIdentificador}</span>
                  <span className="crm-rtz-kpi__label">Con ident.</span>
                </div>
                <div className="crm-rtz-kpi is-success" title="Recetas con cruce contra PLEX">
                  <span className="crm-rtz-kpi__num">{kpis.conCruce}</span>
                  <span className="crm-rtz-kpi__label">Cruce PLEX</span>
                </div>
                <div className="crm-rtz-kpi is-danger" title="Validadas sin cruce con PLEX">
                  <span className="crm-rtz-kpi__num">{kpis.sinCruce}</span>
                  <span className="crm-rtz-kpi__label">Sin cruce</span>
                </div>
              </div>
            ) : null}
          </div>

          {error ? (
            <div className="crm-rc-empty-hero crm-rc-empty-hero--error" role="alert">
              <div className="crm-rc-empty-hero__inner">
                <div className="crm-rc-empty-hero__visual crm-rc-empty-hero__visual--error" aria-hidden="true">
                  <span className="crm-rc-empty-hero__orbit" />
                  <span className="crm-rc-empty-hero__glyph">
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M12 7v6M12 16.5h.01" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                    </svg>
                  </span>
                </div>
                <p className="crm-rc-empty-hero__eyebrow">Error de carga</p>
                <h2 className="crm-rc-empty-hero__title">No se pudieron cargar las recetas</h2>
                <p className="crm-rc-empty-hero__hint">{error || 'Ocurrió un error inesperado. Intentá nuevamente.'}</p>
              </div>
            </div>
          ) : null}

          {!canFetch && !error ? (
            <div className="crm-rc-empty-hero" role="status">
              <div className="crm-rc-empty-hero__inner">
                <div className="crm-rc-empty-hero__visual" aria-hidden="true">
                  <span className="crm-rc-empty-hero__orbit" />
                  <span className="crm-rc-empty-hero__glyph">
                    <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
                      <rect x="4" y="3" width="14" height="18" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M8 8h6M8 12h6M8 16h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      <circle cx="17.5" cy="17.5" r="4.5" fill="#f8fbff" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M16.2 17.5l1 1 2.2-2.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </div>
                <p className="crm-rc-empty-hero__eyebrow">GECLISA ↔ PLEX</p>
                <h2 className="crm-rc-empty-hero__title">Buscá un paciente por DNI</h2>
                <p className="crm-rc-empty-hero__hint">
                  Ingresá un DNI en el buscador superior para ver las recetas emitidas por el sanatorio y sus cruces con PLEX.
                </p>
                <div className="crm-rc-empty-hero__steps" aria-hidden="true">
                  <span className="crm-rc-empty-hero__step">1 · Ingresá el DNI</span>
                  <span className="crm-rc-empty-hero__step">2 · Presioná Buscar</span>
                  <span className="crm-rc-empty-hero__step">3 · Revisá cruces</span>
                </div>
                <p className="crm-rc-empty-hero__nudge">
                  <span className="crm-rc-empty-hero__nudge-ic" aria-hidden="true">↑</span>
                  Usá el buscador de arriba para comenzar
                </p>
              </div>
            </div>
          ) : null}

          {canFetch && !error ? (
            <div className="crm-rc-data">
              <div className="crm-rc-table-wrap crm-tscroll">
                <div className="crm-rc-filters">
                  <div className="crm-rc-filters__row">
                  <span className="crm-rc-filters__meta">
                    <strong>{loading ? '…' : uniqueRows.length}</strong>/<strong>{loading ? '…' : totalRecords}</strong>
                    {formattedDni ? <> · DNI <strong>{formattedDni}</strong></> : null}
                  </span>
                  <div className="crm-rtz-tablesearch crm-rc-tablesearch">
                    <span className="crm-rtz-tablesearch__ic" aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                        <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      className="crm-rtz-tablesearch__input"
                      placeholder="Buscar y Enter…"
                      value={tableSearch}
                      onChange={(e) => setTableSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitTableSearch();
                        }
                      }}
                      aria-label="Buscar en la tabla"
                    />
                    {tableSearch || committedTableSearch ? (
                      <button
                        type="button"
                        className="crm-rtz-tablesearch__clear"
                        onClick={clearTableSearch}
                        aria-label="Limpiar búsqueda"
                      >
                        ✕
                      </button>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="crm-rc-icon-btn crm-rc-icon-btn--primary"
                    onClick={commitTableSearch}
                    title="Buscar en todos los registros"
                    aria-label="Buscar"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                      <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                  <label className="crm-rtz-tablesize crm-rc-tablesize">
                    <div className="crm-rtz-tablesize__wrap">
                      <select
                        className="crm-rtz-tablesize__select"
                        value={pageSize <= 0 ? 'ALL' : String(pageSize)}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPage(1);
                          setPageSize(v === 'ALL' ? 0 : Number(v));
                        }}
                        aria-label="Recetas por página"
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="ALL">Todas</option>
                      </select>
                      <span className="crm-rtz-tablesize__caret" aria-hidden="true">▾</span>
                    </div>
                  </label>

                  <div className="crm-rc-filterchips" role="group" aria-label="Filtrar por estado">
                    {STATUS_FILTER_CHIPS.map((chip) => (
                      <button
                        key={chip.id}
                        type="button"
                        className={`crm-rc-filterchip is-${chip.cls}${statusFilter === chip.id ? ' is-active' : ''}`}
                        onClick={() => handleStatusFilter(chip.id)}
                        aria-pressed={statusFilter === chip.id}
                        title={`Filtrar: ${chip.label}`}
                      >
                        <span className="crm-rc-filterchip__label">{chip.label}</span>
                        {chip.countKey ? (
                          <span className="crm-rc-filterchip__count">{kpis[chip.countKey] ?? 0}</span>
                        ) : null}
                      </button>
                    ))}
                  </div>

                  <div className="crm-rc-filters__dates">
                    <label className="crm-rc-filters__label crm-rc-filters__label--ic" htmlFor="rc-from-date" title="Desde">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                        <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </label>
                    <input
                      id="rc-from-date"
                      type="date"
                      value={pendingFromDate}
                      onChange={(e) => setPendingFromDate(e.target.value)}
                      aria-label="Fecha desde"
                    />
                    <label className="crm-rc-filters__label crm-rc-filters__label--ic" htmlFor="rc-to-date" title="Hasta">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                        <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </label>
                    <input
                      id="rc-to-date"
                      type="date"
                      value={pendingToDate}
                      min={pendingFromDate || undefined}
                      onChange={(e) => setPendingToDate(e.target.value)}
                      aria-label="Fecha hasta"
                    />
                    <button
                      type="button"
                      className="crm-rc-icon-btn crm-rc-icon-btn--primary"
                      onClick={handleApplyDateFilter}
                      title="Aplicar fechas"
                      aria-label="Aplicar fechas"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="crm-rc-icon-btn"
                      onClick={handleClearDateFilter}
                      title="Limpiar fechas"
                      aria-label="Limpiar fechas"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                      </svg>
                    </button>
                    {isAllMode ? (
                      <button
                        type="button"
                        className="crm-rc-icon-btn crm-rc-icon-btn--export"
                        onClick={handleExportCsv}
                        disabled={exporting || !(pendingFromDate || fromDate) || !(pendingToDate || toDate)}
                        title={
                          !(pendingFromDate || fromDate) || !(pendingToDate || toDate)
                            ? 'Elegí fecha Desde y Hasta para descargar'
                            : 'Descargar CSV (Excel) del rango seleccionado'
                        }
                        aria-label="Descargar CSV"
                      >
                        {exporting ? (
                          <span className="crm-gd-spinner crm-rc-export-btn__spinner" aria-hidden="true" />
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M12 4v10M8 11l4 4 4-4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M5 20h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                          </svg>
                        )}
                      </button>
                    ) : null}
                  </div>
                </div>
                </div>
                {exportError ? (
                  <div className="crm-rc-export-error" role="alert">{exportError}</div>
                ) : null}

                {loading && canFetch ? (
                  <div className="crm-gd-loading" role="status" aria-live="polite">
                    <div className="crm-gd-spinner" aria-hidden="true" />
                    <div className="crm-gd-loading__title">
                      {committedTableSearch
                        ? `Buscando "${committedTableSearch}"…`
                        : isAllMode && !data
                          ? 'Cargando recetas…'
                          : `Cargando página ${currentPage}${totalPages ? ` de ${totalPages}` : ''}…`}
                    </div>
                  </div>
                ) : null}

                {!loading && uniqueRows.length === 0 ? (
                  <div className="crm-gd-empty">
                    {isAllMode
                      ? 'No se encontraron recetas en la vista global con los filtros aplicados.'
                      : 'No se encontraron recetas para este DNI en el cruce GECLISA ↔ PLEX.'}
                  </div>
                ) : !loading ? (
                  <PaginatedTable
                    flatWrapper
                    tableClassName="crm-rc-datatable"
                    data={uniqueRows}
                    columns={columns}
                    extraColumns={extraColumns}
                    pageSize={pageSize <= 0 ? (totalRecords || uniqueRows.length || 1) : pageSize}
                    page={currentPage}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    paginationLabel={`Página ${currentPage} de ${totalPages}`}
                  />
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}

export default RecetasCruzadas;

